<script setup lang="ts">
import { ref, watch } from 'vue'
import type { RmiCategory } from '../../types'
import type { CustomRmiLink } from '../../api/tecdocApi'
import { useNavigationStore } from '../../stores/navigationStore'
import EditableLabel from '../shared/EditableLabel.vue'
import ShortcutCard from '../content/ShortcutCard.vue'

const props = defineProps<{
  categories: RmiCategory[]
}>()

const emit = defineEmits<{
  selectMainGroup: [id: number, name: string]
  selectSubGroup: [mainGroupId: number, subGroupId: number, name: string]
  selectItem: [mainGroupId: number, subGroupId: number, itemMpId: number, name: string]
  selectCustomLink: [link: CustomRmiLink]
  addCustomLinkRequested: []
  hideCategoryRequested: [id: string, label: string]
  unhideCategoryRequested: [id: string]
}>()

const navStore = useNavigationStore()

async function removeCustomLink(link: CustomRmiLink) {
  if (!confirm(`Fjerne "${link.label}"?`)) return
  try {
    await navStore.removeCustomRmiLink(
      link.categoryId,
      link.mainGroupId ?? null,
      link.subGroupId ?? null,
    )
  } catch (err) {
    alert('Kunne ikke slette: ' + ((err as Error).message || ''))
  }
}

const expandedMainGroup = ref<number | null>(null)
const expandedSubGroup = ref<number | null>(null)

// Sync menu state when SVG clicks change the navigation store
watch(
  () => navStore.mainGroupId,
  (mgId) => {
    if (mgId !== null) {
      expandedMainGroup.value = Number(mgId)
      expandedSubGroup.value = null
    }
  },
)

watch(
  () => navStore.subGroupId,
  (sgId) => {
    if (sgId !== null) {
      expandedSubGroup.value = Number(sgId)
    }
  },
)

// When navigating back to vehicle level, collapse menu
watch(
  () => navStore.currentLevel,
  (level) => {
    if (level === 'vehicle') {
      expandedMainGroup.value = null
      expandedSubGroup.value = null
    }
  },
)

function toggleMainGroup(mg: RmiCategory) {
  if (expandedMainGroup.value === mg.mainGroupId) {
    expandedMainGroup.value = null
  } else {
    expandedMainGroup.value = mg.mainGroupId
    expandedSubGroup.value = null
  }
  emit('selectMainGroup', mg.mainGroupId, mg.mainGroupName)
}

function toggleSubGroup(mgId: number, sg: { subGroupId: number; subGroupName: string }) {
  if (expandedSubGroup.value === sg.subGroupId) {
    expandedSubGroup.value = null
  } else {
    expandedSubGroup.value = sg.subGroupId
  }
  emit('selectSubGroup', mgId, sg.subGroupId, sg.subGroupName)
}

function selectItem(mgId: number, sgId: number, itemMpId: number, text: string) {
  emit('selectItem', mgId, sgId, itemMpId, text)
}

function isHidden(id: string): boolean {
  return navStore.hiddenIds.includes(id)
}

function onVisibilityToggle(id: string, label: string) {
  if (isHidden(id)) {
    if (confirm('Gjenopprette synlighet for dette elementet?')) {
      emit('unhideCategoryRequested', id)
    }
  } else {
    emit('hideCategoryRequested', id, label)
  }
}
</script>

<template>
  <div class="category-menu">
    <!-- Editor-pinned custom category shortcuts (mirror of the card bar above the SVG) -->
    <div
      v-if="navStore.customRmiLinksForCurrentScope.length > 0 || navStore.canEditCategories"
      class="category-menu__custom"
    >
      <div class="category-menu__custom-header">Egne snarveier</div>
      <div class="category-menu__custom-grid">
        <ShortcutCard
          v-for="link in navStore.customRmiLinksForCurrentScope"
          :key="`${link.categoryId}-${link.mainGroupId ?? ''}-${link.subGroupId ?? ''}`"
          :link="link"
          :editable="navStore.canEditCategories"
          @click="emit('selectCustomLink', link)"
          @remove="removeCustomLink(link)"
        />
        <button
          v-if="navStore.canEditCategories"
          type="button"
          class="category-menu__custom-add-card"
          @click="emit('addCustomLinkRequested')"
        >
          <span class="category-menu__custom-add-icon">+</span>
          <span class="category-menu__custom-add-label">Legg til</span>
        </button>
      </div>
    </div>

    <ul class="category-menu__list">
      <li
        v-for="mg in categories"
        :key="mg.mainGroupId"
        class="category-menu__group"
        :class="{
          'category-menu__group--expanded': expandedMainGroup === mg.mainGroupId,
          'category-menu__group--hidden': isHidden(`mg:${mg.mainGroupId}`),
        }"
      >
        <div class="category-menu__group-row">
          <button class="category-menu__group-btn" @click="toggleMainGroup(mg)">
            <EditableLabel
              class="category-menu__group-name"
              :label-key="`mg:${mg.mainGroupId}`"
              :fallback="mg.mainGroupName"
            />
            <span class="category-menu__arrow">&#9656;</span>
          </button>
          <button
            v-if="navStore.canEditCategories"
            type="button"
            class="category-menu__visibility-btn"
            :title="isHidden(`mg:${mg.mainGroupId}`) ? 'Vis kategori' : 'Skjul kategori'"
            @click.stop="onVisibilityToggle(`mg:${mg.mainGroupId}`, mg.mainGroupName)"
          >
            <svg v-if="isHidden(`mg:${mg.mainGroupId}`)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>

        <ul v-if="expandedMainGroup === mg.mainGroupId && mg.subGroups?.length" class="category-menu__sublist">
          <li
            v-for="sg in mg.subGroups"
            :key="sg.subGroupId"
            class="category-menu__subgroup"
            :class="{
              'category-menu__subgroup--expanded': expandedSubGroup === sg.subGroupId,
              'category-menu__subgroup--hidden': isHidden(`sg:${mg.mainGroupId}:${sg.subGroupId}`),
            }"
          >
            <div class="category-menu__subgroup-row">
              <button class="category-menu__subgroup-btn" @click="toggleSubGroup(mg.mainGroupId, sg)">
                <EditableLabel
                  :label-key="`sg:${mg.mainGroupId}:${sg.subGroupId}`"
                  :fallback="sg.subGroupName"
                />
                <span v-if="sg.items?.length" class="category-menu__arrow">&#9656;</span>
              </button>
              <button
                v-if="navStore.canEditCategories"
                type="button"
                class="category-menu__visibility-btn"
                :title="isHidden(`sg:${mg.mainGroupId}:${sg.subGroupId}`) ? 'Vis kategori' : 'Skjul kategori'"
                @click.stop="onVisibilityToggle(`sg:${mg.mainGroupId}:${sg.subGroupId}`, sg.subGroupName)"
              >
                <svg v-if="isHidden(`sg:${mg.mainGroupId}:${sg.subGroupId}`)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>

            <ul v-if="expandedSubGroup === sg.subGroupId && sg.items?.length" class="category-menu__items">
              <li
                v-for="item in sg.items"
                :key="item.itemMpId"
                class="category-menu__item"
                :class="{ 'category-menu__item--hidden': isHidden(`item:${mg.mainGroupId}:${sg.subGroupId}:${item.itemMpId}`) }"
              >
                <div class="category-menu__item-row">
                  <button
                    class="category-menu__item-btn"
                    @click="selectItem(mg.mainGroupId, sg.subGroupId, item.itemMpId, item.itemMpText)"
                  >
                    <EditableLabel
                      :label-key="`item:${mg.mainGroupId}:${sg.subGroupId}:${item.itemMpId}`"
                      :fallback="item.itemMpText"
                    />
                  </button>
                  <button
                    v-if="navStore.canEditCategories"
                    type="button"
                    class="category-menu__visibility-btn"
                    :title="isHidden(`item:${mg.mainGroupId}:${sg.subGroupId}:${item.itemMpId}`) ? 'Vis kategori' : 'Skjul kategori'"
                    @click.stop="onVisibilityToggle(`item:${mg.mainGroupId}:${sg.subGroupId}:${item.itemMpId}`, item.itemMpText)"
                  >
                    <svg v-if="isHidden(`item:${mg.mainGroupId}:${sg.subGroupId}:${item.itemMpId}`)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.category-menu {
  background: #f5f5f5;
  border-radius: 10px;
  padding: 0.5rem;
  max-height: 70vh;
  overflow-y: auto;

  &__list, &__sublist, &__items {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__group-row, &__subgroup-row, &__item-row {
    display: flex;
    align-items: center;
  }

  &__group-btn, &__subgroup-btn, &__item-btn {
    flex: 1;
    min-width: 0;
    text-align: left;
    background: none;
    border: none;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px;
    font-size: 0.9rem;
    text-transform: capitalize;

    &:hover { background: #e8e8e8; }
  }

  &__visibility-btn {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    padding: 4px;
    margin-right: 4px;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    color: #9ca3af;
    opacity: 0;
    transition: opacity 0.15s, color 0.15s;

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }

    &:hover {
      color: #002868;
      background: #e0ecff;
    }
  }

  &__group-row:hover &__visibility-btn,
  &__subgroup-row:hover &__visibility-btn,
  &__item-row:hover &__visibility-btn {
    opacity: 1;
  }

  // Always show icon for hidden items
  &__group--hidden &__visibility-btn,
  &__subgroup--hidden &__visibility-btn,
  &__item--hidden &__visibility-btn {
    opacity: 1;
    color: #dc2626;
  }

  &__group-btn {
    font-weight: 600;
    font-size: 0.95rem;
  }

  &__group--hidden > &__group-row > &__group-btn {
    opacity: 0.5;
    font-style: italic;
  }

  &__group--expanded > &__group-row > &__group-btn,
  &__group--expanded > &__group-btn {
    background: #002868;
    color: white;

    &:hover { background: #001d4f; }

    .category-menu__arrow { transform: rotate(90deg); }
  }

  &__sublist {
    padding-left: 0.5rem;
    margin-top: 2px;
  }

  &__subgroup-btn {
    font-size: 0.85rem;
    padding: 6px 10px;
  }

  &__subgroup--hidden > &__subgroup-row > &__subgroup-btn {
    opacity: 0.5;
    font-style: italic;
  }

  &__subgroup--expanded > &__subgroup-row > &__subgroup-btn,
  &__subgroup--expanded > &__subgroup-btn {
    background: #e0e7ff;
    font-weight: 600;

    .category-menu__arrow { transform: rotate(90deg); }
  }

  &__items {
    padding-left: 0.75rem;
    margin-top: 2px;
  }

  &__item-btn {
    font-size: 0.8rem;
    padding: 5px 8px;
    color: #444;
  }

  &__item--hidden > &__item-row > &__item-btn {
    opacity: 0.5;
    font-style: italic;
  }

  &__arrow {
    transition: transform 0.15s;
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  &__custom {
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  &__custom-header {
    padding: 4px 4px 6px;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
    font-weight: 700;
  }

  &__custom-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 0.5rem;
    padding: 0 4px;
  }

  &__custom-add-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    width: 100%;
    min-height: 96px;
    padding: 0.5rem;
    background: white;
    color: #002868;
    border: 2px dashed #002868;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: #e0ecff;
    }
  }

  &__custom-add-icon {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
  }

  &__custom-add-label {
    font-size: 0.75rem;
    font-weight: 600;
  }
}
</style>
